/**
 * Shared error type guards for App Home.
 * Used by both Categories and Accounts views for consistent error handling.
 */

import { toast } from 'sonner';

import { WriteRefusedError } from './apiProblemError';
import { logger } from './logger';

/**
 * `FetchError` is the canonical structured browser error shape shared with
 * U7 mini-apps (see `mini-apps/_shared/fetch-error.ts`'s `FetchErrorLike`,
 * a superset with `status`). It lives in `fetch-error-types.ts` — a
 * dependency-free leaf — rather than here, because this module transitively
 * imports `./logger`, which uses `import.meta` and is therefore unimportable
 * from the mini-app side's `tsconfig.tests.json` (commonjs) type-check.
 * Re-exported here for existing consumers that import `FetchError` from
 * this module.
 */
import type { FetchError } from '../../../../mini-apps/_shared/fetch-error-types';

export type { FetchError };

type ErrorKind = 'network' | 'auth' | 'forbidden' | 'conflict' | 'unknown';

/** Narrow to FetchError — requires a `code` string property. */
export function isFetchError(err: unknown): err is FetchError {
  return Boolean(err && typeof err === 'object' && typeof (err as any).code === 'string');
}

/** Map from error.code to ErrorKind (code-dispatch, checked before regex). */
const CODE_KIND_MAP: Record<string, ErrorKind> = {
  '401': 'auth',
  '403': 'forbidden',
  '409': 'conflict',
  NETWORK_ERROR: 'network',
  // `ApiProblemError`'s `kind: 'aborted'` (a client-side fetch timeout/abort,
  // `fetchJson`) is a network failure, not an unclassified one — without
  // this, the global MutationCache/QueryCache toast fell through to the
  // generic "Something went wrong" copy instead of the shared network
  // sentence (`NETWORK_FAILURE_MESSAGE`) that `notifyError`'s `rawMessage`
  // branch would otherwise surface for any other 'network'-kind error.
  ABORTED: 'network',
  SF_REAUTH_REQUIRED: 'auth',
  READ_ONLY_IMPERSONATION: 'forbidden',
  EXTERNAL_WRITE_BLOCKED: 'forbidden',
};

export function classifyError(err: unknown): ErrorKind {
  try {
    if (!err) return 'unknown';

    // Code-first dispatch (structured FetchError)
    if (isFetchError(err)) {
      return CODE_KIND_MAP[err.code] ?? 'unknown';
    }

    // Legacy regex fallback for un-migrated bare-Error throwers
    const msg = String((err as any)?.message || '');
    if (/401|unauth/i.test(msg)) return 'auth';
    if (/403|forbidden/i.test(msg)) return 'forbidden';
    if (/409|conflict/i.test(msg)) return 'conflict';
    if (/network|fetch|failed/i.test(msg)) return 'network';
  } catch {}
  return 'unknown';
}

/**
 * Deterministic (non-cryptographic) hash of a toast message into a stable
 * sonner `id`. Identical rendered text -> identical id -> sonner collapses
 * repeats into one toast instead of stacking (GTMOPS-1950 Unit 2). Never
 * derived from Math.random()/Date.now() — that would defeat dedup by
 * definition, since every call would get a fresh id.
 */
export function toastIdForMessage(message: string): string {
  let hash = 5381;
  for (let i = 0; i < message.length; i++) {
    hash = (hash * 33) ^ message.charCodeAt(i);
  }
  return `notify-error-${(hash >>> 0).toString(36)}`;
}

export function notifyError(err: unknown, context?: string): void {
  try {
    const kind = classifyError(err);
    const rawMessage = String((err as any)?.message || '');

    // Prefer structured userMessage; fall back to bare message.
    // For `unknown` kinds we deliberately DO NOT surface the raw message:
    // FetchError.message is an internal string like `some_code:502` and bare
    // throws can carry ZodError text. The generic MutationCache now toasts
    // every mutation rejection, so an unclassified error must show safe copy —
    // the real message is preserved in the dev breadcrumb below.
    let msg: string;
    if (isFetchError(err) && err.userMessage) {
      msg = err.userMessage;
    } else if (kind === 'unknown') {
      msg = 'Something went wrong. Please try again.';
    } else {
      msg = rawMessage || 'Unknown error';
    }

    // Diagnostic breadcrumb for developers — the classification tag is NOT
    // user-facing. Users see the clean `msg` in the toast; `rawMessage` keeps
    // the underlying error inspectable even when the toast is generic.
    logger.debug({ context: context || 'error', kind, msg, rawMessage }, 'notifyError');

    // Render a reconnect action when oauthUrl is present (reauth case)
    if (isFetchError(err) && err.oauthUrl) {
      const oauthUrl = err.oauthUrl;
      toast.error(msg, {
        id: 'salesforce-reauth-required',
        duration: Infinity,
        action: {
          label: 'Reconnect',
          onClick: () => {
            window.location.href = oauthUrl;
          },
        },
      });
    } else {
      toast.error(msg, { id: toastIdForMessage(msg) });
    }
  } catch {}
}

/**
 * A call site's own failure toast: the error's message, else `fallback`.
 * Skips a `WriteRefusedError`, which `apiFetch` already announced once.
 */
export function toastUnlessRefused(err: unknown, fallback: string): void {
  if (err instanceof WriteRefusedError) return;
  toast.error(err instanceof Error && err.message ? err.message : fallback);
}

export function isUserNotMappedError(error: unknown): error is { code: 'user_not_mapped'; email?: string } {
  return Boolean(error && typeof error === 'object' && (error as any).code === 'user_not_mapped');
}

export function isForbiddenError(error: unknown): error is { code: 'forbidden' } {
  return Boolean(error && typeof error === 'object' && (error as any).code === 'forbidden');
}
